import React, { useState } from 'react';
import { MEMBERSHIP_TYPES, FAMILY_ROLES, AREAS } from '../../lib/constants';
import { useDistricts, useFamilies, useGroups, createFamily } from '../../hooks/useMembers';
import { COUNTRY_CODES } from '../../lib/countryCodes'; // <-- import the full list from a separate file, or paste it directly

const MARITAL = ['Single','Married','Widowed','Divorced','Separated'];

export default function MemberForm({ initial={}, onSubmit, loading, submitLabel='Save Member' }) {
  const districts = useDistricts();
  const { families, refetch: refetchFamilies } = useFamilies();
  const groups    = useGroups();

  // State: replace dob with birth_year
  const [form, setForm] = useState({
    first_name: initial.first_name ?? '',
    last_name: initial.last_name ?? '',
    other_names: initial.other_names ?? '',
    birth_year: initial.birth_year ?? '',  // <-- new field
    gender: initial.gender ?? '',
    phone_country: initial.phone_country ?? '+254',
    phone: initial.phone ?? '',
    email: initial.email ?? '',
    id_number: initial.id_number ?? '',
    marital_status: initial.marital_status ?? '',
    district_id: initial.district_id ?? '',
    family_id: initial.family_id ?? '',
    family_role: initial.family_role ?? 'member',
    membership_type: initial.membership_type ?? 'Pending',
    area: initial.area ?? '',
    joined_date: initial.joined_date ?? new Date().toISOString().split('T')[0],
    is_child: initial.is_child ?? false,
    is_baptised: initial.is_baptised ?? false,
    baptism_date: initial.baptism_date ?? '',
    is_confirmed: initial.is_confirmed ?? false,
    confirmation_date: initial.confirmation_date ?? '',
    is_deceased: initial.is_deceased ?? false,
    death_date: initial.death_date ?? '',
    is_wedded: initial.is_wedded ?? false,
    wedding_date: initial.wedding_date ?? '',
    spouse_name: initial.spouse_name ?? '',
    selectedGroups: initial.selectedGroups ?? [],
  });

  const [newFamily,   setNewFamily]   = useState('');
  const [addFamily,   setAddFamily]   = useState(false);
  const [creatingFam, setCreatingFam] = useState(false);
  const [phoneError,  setPhoneError]  = useState('');

  const setV = (k) => (e) => setForm(f=>({...f,[k]:e.target.value}));
  const setC = (k) => (e) => setForm(f=>({...f,[k]:e.target.checked}));

  function validatePhone(v) {
    if (!v || v.trim() === '') { setPhoneError(''); return true; }
    const d = v.replace(/\D/g,'');
    if (d.length < 9) { setPhoneError('Too short — minimum 9 digits'); return false; }
    if (d.length > 11) { setPhoneError('Too long — maximum 11 digits'); return false; }
    setPhoneError(''); return true;
  }

  function toggleGroup(id) {
    setForm(f => ({
      ...f,
      selectedGroups: f.selectedGroups.includes(id)
        ? f.selectedGroups.filter(g=>g!==id)
        : [...f.selectedGroups, id],
    }));
  }

  async function handleCreateFamily() {
    if (!newFamily.trim()) return; setCreatingFam(true);
    const fam = await createFamily(newFamily.trim(), form.district_id||null);
    if (fam) { await refetchFamilies(); setForm(f=>({...f,family_id:fam.id})); setAddFamily(false); setNewFamily(''); }
    setCreatingFam(false);
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!validatePhone(form.phone)) return;
    const { selectedGroups, phone_country, ...rest } = form;
    // Sanitize dates and UUIDs
    ['baptism_date','confirmation_date','death_date','wedding_date','joined_date'].forEach(k=>{ if(rest[k]==='') rest[k]=null; });
    ['district_id','family_id'].forEach(k=>{ if(!rest[k]) rest[k]=null; });
    // Combine country code + number (strip leading 0)
    if (rest.phone && rest.phone.trim() !== '') {
      rest.phone = phone_country + rest.phone.replace(/^0+/,'');
    } else {
      rest.phone = '';
    }
    // Ensure birth_year is integer or null
    rest.birth_year = rest.birth_year ? parseInt(rest.birth_year, 10) : null;
    if (rest.is_deceased) rest.status = 'deceased';
    onSubmit(rest, selectedGroups);
  }

  const Sec = ({text,icon})=>(
    <div style={{fontSize:11,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',letterSpacing:.5,margin:'20px 0 10px',paddingBottom:6,borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:6}}>
      {icon}<span>{text}</span>
    </div>
  );

  const EventRow = ({flag,flagKey,dateKey,dateLabel,accent='blue',extra})=>{
    const c={blue:{bd:'var(--blue)',bg:'var(--blue-light)',ck:'var(--blue)'},amber:{bd:'#C47A00',bg:'var(--warning-bg)',ck:'#C47A00'},red:{bd:'var(--red)',bg:'var(--red-light)',ck:'var(--red)'}}[accent]||{bd:'var(--blue)',bg:'var(--blue-light)',ck:'var(--blue)'};
    return(
      <div style={{background:form[flagKey]?c.bg:'var(--bg)',border:`1.5px solid ${form[flagKey]?c.bd:'var(--border)'}`,borderRadius:10,padding:'13px 16px',marginBottom:10,transition:'all .15s'}}>
        <label style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer',marginBottom:form[flagKey]?12:0}}>
          <input type="checkbox" checked={form[flagKey]||false} onChange={setC(flagKey)} style={{width:18,height:18,cursor:'pointer',accentColor:c.ck}}/>
          <span style={{fontWeight:700,fontSize:14,color:form[flagKey]?c.ck:'var(--text)'}}>{flag}</span>
        </label>
        {form[flagKey]&&(
          <div className="grid-2" style={{marginTop:4}}>
            <div className="form-group" style={{marginBottom:0}}>
              <label className="form-label">{dateLabel}</label>
              <input className="form-input" type="date" value={form[dateKey]||''} onChange={setV(dateKey)}/>
            </div>
            {extra}
          </div>
        )}
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit}>

      {/* Member number badge */}
      {initial.member_number&&(
        <div style={{background:'linear-gradient(135deg,var(--blue-dark),var(--blue))',borderRadius:10,padding:'12px 18px',marginBottom:20,display:'flex',alignItems:'center',justifyContent:'space-between',color:'white'}}>
          <div>
            <div style={{fontSize:10,opacity:.7,letterSpacing:.8,textTransform:'uppercase',marginBottom:2}}>Member Number</div>
            <div style={{fontSize:22,fontWeight:900,letterSpacing:2}}>{initial.member_number}</div>
          </div>
          <div style={{fontSize:28,opacity:.3}}>✝</div>
        </div>
      )}

      <Sec text="Personal Information" icon="👤"/>
      <div className="grid-2">
        <div className="form-group"><label className="form-label">First Name *</label><input className="form-input" value={form.first_name} onChange={setV('first_name')} required/></div>
        <div className="form-group"><label className="form-label">Last Name *</label><input className="form-input" value={form.last_name} onChange={setV('last_name')} required/></div>
        <div className="form-group"><label className="form-label">Other Names</label><input className="form-input" value={form.other_names} onChange={setV('other_names')} placeholder="Middle names"/></div>
        {/* Replace Date of Birth with Year of Birth */}
        <div className="form-group">
          <label className="form-label">Year of Birth</label>
          <input
            className="form-input"
            type="number"
            value={form.birth_year}
            onChange={setV('birth_year')}
            placeholder="e.g. 1990"
            min="1900"
            max="2035"
            step="1"
          />
        </div>
        <div className="form-group"><label className="form-label">Gender</label><select className="form-select" value={form.gender} onChange={setV('gender')}><option value="">Select…</option><option>Male</option><option>Female</option></select></div>
        <div className="form-group"><label className="form-label">Marital Status</label><select className="form-select" value={form.marital_status} onChange={setV('marital_status')}><option value="">Select…</option>{MARITAL.map(s=><option key={s}>{s}</option>)}</select></div>
        <div className="form-group"><label className="form-label">National ID / Passport</label><input className="form-input" value={form.id_number} onChange={setV('id_number')} placeholder="ID or Passport no."/></div>
        <div className="form-group"><label className="form-label">Email Address</label><input className="form-input" type="email" value={form.email} onChange={setV('email')} placeholder="email@example.com"/></div>
      </div>

      {/* Phone with country code */}
      <div className="form-group">
        <label className="form-label">Phone Number <span style={{color:'var(--text3)',fontWeight:400,textTransform:'none',letterSpacing:0,fontSize:11}}>— optional for children · must be unique if provided</span></label>
        <div style={{display:'flex',gap:8}}>
          <select className="form-select" style={{width:195,flexShrink:0}} value={form.phone_country} onChange={setV('phone_country')}>
            {COUNTRY_CODES.map(c=><option key={c.code} value={c.code}>{c.code} {c.country}</option>)}
          </select>
          <div style={{flex:1}}>
            <input className={`form-input${phoneError?' error':''}`} value={form.phone}
              onChange={e=>{setV('phone')(e);if(phoneError)validatePhone(e.target.value);}}
              onBlur={e=>validatePhone(e.target.value)}
              placeholder="7XXXXXXXX (without leading 0)"/>
            {phoneError&&<div className="form-error">⚠️ {phoneError}</div>}
            {form.phone&&!phoneError&&form.phone.trim()!==''&&<div style={{fontSize:11,color:'var(--success)',marginTop:3}}>✓ Full number: {form.phone_country}{form.phone.replace(/^0+/,'')}</div>}
            {!form.phone&&<div style={{fontSize:11,color:'var(--text3)',marginTop:3}}>Leave blank for children or members without a phone number.</div>}
          </div>
        </div>
      </div>

      <Sec text="Church Information" icon="✝️"/>
      <div className="grid-2">
        <div className="form-group"><label className="form-label">District *</label><select className="form-select" value={form.district_id} onChange={setV('district_id')} required><option value="">Select district…</option>{districts.map(d=><option key={d.id} value={d.id}>{d.name}</option>)}</select></div>
        <div className="form-group"><label className="form-label">Membership Type *</label><select className="form-select" value={form.membership_type} onChange={setV('membership_type')} required>{MEMBERSHIP_TYPES.map(t=><option key={t}>{t}</option>)}</select></div>
        <div className="form-group"><label className="form-label">Area / Village *</label><select className="form-select" value={form.area} onChange={setV('area')} required>{AREAS.map(t=><option key={t}>{t}</option>)}</select></div>
        <div className="form-group"><label className="form-label">Date Joined</label><input className="form-input" type="date" value={form.joined_date} onChange={setV('joined_date')}/></div>
      </div>
      <div className="form-group">
        <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:13,fontWeight:500,padding:'10px 14px',background:'var(--bg)',borderRadius:8,border:'1.5px solid var(--border)'}}>
          <input type="checkbox" checked={form.is_child} onChange={setC('is_child')} style={{accentColor:'var(--blue)'}}/>
          👶 This is a baptised child — tracks to Full Member on confirmation
        </label>
      </div>

      <Sec text="Family Household" icon="🏠"/>
      <div className="grid-2">
        <div className="form-group">
          <label className="form-label">Family Household</label>
          <select className="form-select" value={form.family_id} onChange={setV('family_id')}>
            <option value="">None / Create new</option>
            {families.map(f=><option key={f.id} value={f.id}>{f.name} ({f.districts?.name||'—'})</option>)}
          </select>
          <button type="button" className="btn btn-ghost btn-sm" style={{fontSize:12,color:'var(--blue)',marginTop:6}} onClick={()=>setAddFamily(a=>!a)}>
            {addFamily?'↑ Cancel':'+ Create new household'}
          </button>
          {addFamily&&(
            <div style={{display:'flex',gap:8,marginTop:8}}>
              <input className="form-input" value={newFamily} onChange={e=>setNewFamily(e.target.value)} placeholder="e.g. Kariuki Family"/>
              <button type="button" className="btn btn-blue btn-sm" onClick={handleCreateFamily} disabled={creatingFam}>{creatingFam?'…':'Create'}</button>
            </div>
          )}
        </div>
        <div className="form-group"><label className="form-label">Role in Family</label><select className="form-select" value={form.family_role} onChange={setV('family_role')}>{FAMILY_ROLES.map(r=><option key={r} value={r}>{r.charAt(0).toUpperCase()+r.slice(1)}</option>)}</select></div>
      </div>

      <Sec text="Groups & Ministries" icon="🙏"/>
      <div style={{display:'flex',flexWrap:'wrap',gap:8,marginBottom:20}}>
        {groups.map(g=>{
          const sel=(form.selectedGroups||[]).includes(g.id);
          return(<button key={g.id} type="button" onClick={()=>toggleGroup(g.id)} style={{padding:'5px 14px',borderRadius:100,border:'1.5px solid',cursor:'pointer',fontFamily:'inherit',fontSize:12,fontWeight:600,transition:'all .14s',borderColor:sel?'var(--blue)':'var(--border)',background:sel?'var(--blue-light)':'white',color:sel?'var(--blue-dark)':'var(--text3)'}}>{g.name}</button>);
        })}
      </div>

      <Sec text="Life Events" icon="📖"/>
      <div style={{background:'var(--blue-light)',borderRadius:10,padding:'11px 16px',marginBottom:14,fontSize:12,color:'var(--blue-dark)'}}>
        ℹ️ Checked events appear automatically in the <strong>Life Events Tracking</strong> page and in annual reports.
      </div>

      <EventRow flag="💧 Baptised"        flagKey="is_baptised"  dateKey="baptism_date"     dateLabel="Date of Baptism"     accent="blue"
        extra={form.is_child?<div className="form-group" style={{marginBottom:0}}><label className="form-label">Parent / Guardian Names</label><input className="form-input" value={form.spouse_name||''} onChange={setV('spouse_name')} placeholder="e.g. John & Mary Kamau"/></div>:null}/>
      <EventRow flag="✝️ Confirmed"        flagKey="is_confirmed" dateKey="confirmation_date" dateLabel="Date of Confirmation" accent="blue"/>
      <EventRow flag="💍 Wedded / Married" flagKey="is_wedded"    dateKey="wedding_date"      dateLabel="Date of Wedding"      accent="amber"
        extra={<div className="form-group" style={{marginBottom:0}}><label className="form-label">Spouse Name</label><input className="form-input" value={form.spouse_name||''} onChange={setV('spouse_name')} placeholder="Spouse's full name"/></div>}/>

      {/* Deceased */}
      <div style={{background:form.is_deceased?'#FFEBEE':'var(--bg)',border:`1.5px solid ${form.is_deceased?'var(--red)':'var(--border)'}`,borderRadius:10,padding:'13px 16px',marginBottom:10,transition:'all .15s'}}>
        <label style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer',marginBottom:form.is_deceased?12:0}}>
          <input type="checkbox" checked={form.is_deceased||false} onChange={setC('is_deceased')} style={{width:18,height:18,cursor:'pointer',accentColor:'var(--red)'}}/>
          <span style={{fontWeight:700,fontSize:14,color:form.is_deceased?'var(--red-dark)':'var(--text)'}}>⚰️ Deceased</span>
          {form.is_deceased&&<span style={{fontSize:11,color:'var(--red)'}}>— member will be greyed out in all lists</span>}
        </label>
        {form.is_deceased&&<div className="grid-2" style={{marginTop:4}}><div className="form-group" style={{marginBottom:0}}><label className="form-label">Date of Death</label><input className="form-input" type="date" value={form.death_date||''} onChange={setV('death_date')}/></div></div>}
      </div>

      <div style={{marginTop:24,paddingTop:16,borderTop:'1px solid var(--border)'}}>
        <button type="submit" className="btn btn-primary" disabled={loading} style={{minWidth:160}}>
          {loading?'Saving…':submitLabel}
        </button>
      </div>
    </form>
  );
}